import { format } from "date-fns";
import {
  ANALYTICS_DEFAULT_LOOKBACK_DAYS,
  DEFAULT_LOOKBACK_DAYS,
} from "@/lib/constants";

/** Max UTC day partitions walked for a createdAt DateIndex range. */
export const MAX_DATE_INDEX_DAYS = 62;

const STORED_TIMESTAMP_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Human-readable formatting of a stored timestamp string without applying
 * timezone conversion. Uses the wall-clock digits as written (ignores Z/offset).
 */
export function formatStoredTimestamp(
  value: string,
  patternWithTime = "MMM d, yyyy HH:mm:ss",
  patternDateOnly = "MMM d, yyyy",
): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(STORED_TIMESTAMP_RE);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const hasTime = hour !== undefined;
    // Build a local Date from the literal digits so date-fns format() does not
    // shift the clock when the source string carried a Z or offset.
    const mirror = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hasTime ? Number(hour) : 0,
      hasTime ? Number(minute) : 0,
      hasTime ? Number(second) : 0,
    );
    if (Number.isNaN(mirror.getTime())) return null;
    return format(mirror, hasTime ? patternWithTime : patternDateOnly);
  }

  const parsed = parseFlexibleDate(trimmed);
  if (!parsed) return null;
  return format(parsed, patternWithTime);
}

/**
 * Parses a DynamoDB date-ish attribute (ISO string, date-only string,
 * epoch seconds, or epoch milliseconds) into a `Date`, returning `null`
 * for anything that cannot be confidently parsed rather than an
 * "Invalid Date" instance.
 */
export function parseFlexibleDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (trimmed === "") return null;

  if (/^\d+$/.test(trimmed)) {
    const numeric = Number.parseInt(trimmed, 10);
    const ms = trimmed.length <= 10 ? numeric * 1000 : numeric;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toEpochMillis(date: Date | null): number | null {
  if (!date) return null;
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
}

export function toIsoStringOrNull(date: Date | null): string | null {
  if (!date) return null;
  const time = date.getTime();
  return Number.isNaN(time) ? null : date.toISOString();
}

export function toEventDate(date: Date | null): string | null {
  const iso = toIsoStringOrNull(date);
  return iso ? iso.slice(0, 10) : null;
}

export function toEventHour(date: Date | null): string | null {
  const iso = toIsoStringOrNull(date);
  return iso ? iso.slice(0, 13) : null;
}

export function isValidDate(date: Date | null): date is Date {
  return date !== null && !Number.isNaN(date.getTime());
}

/**
 * When from/to are the same instant (typical single-day calendar click), expand
 * to a 24h inclusive window starting at that instant.
 */
export function expandCollapsedInstantRange(
  from: Date | undefined,
  to: Date | undefined,
): { from: Date | undefined; to: Date | undefined } {
  if (!from && !to) return { from: undefined, to: undefined };
  const start = from ?? to!;
  const end = to ?? from!;
  if (start.getTime() !== end.getTime()) return { from, to };
  return {
    from: new Date(start.getTime()),
    to: new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1),
  };
}

/**
 * Calendar pickers yield local-midnight `Date`s for the clicked cell.
 * Convert those Y/M/D components to inclusive UTC day bounds so the query
 * matches UTC timestamps shown in the table / DateIndex partitions.
 */
export function startOfUtcCalendarDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}

export function endOfUtcCalendarDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

/** Local midnight for the UTC calendar day — for highlighting in day pickers. */
export function utcInstantToLocalCalendarDate(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** `HH:mm:ss` from the UTC clock of an instant (matches stored log timestamps). */
export function formatUtcTimeOfDay(date: Date): string {
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  const ss = String(date.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/**
 * Combine a calendar picker's local Y/M/D with a wall-clock `HH:mm[:ss]` string
 * into a UTC instant (same convention as `startOfUtcCalendarDay`).
 */
export function combineUtcCalendarDateAndTime(
  calendarDate: Date,
  time: string,
  options?: { endOfSecond?: boolean },
): Date {
  const segments = time.split(":");
  const hours = Number.parseInt(segments[0] ?? "0", 10) || 0;
  const minutes = Number.parseInt(segments[1] ?? "0", 10) || 0;
  const seconds = Number.parseInt(segments[2] ?? "0", 10) || 0;
  return new Date(
    Date.UTC(
      calendarDate.getFullYear(),
      calendarDate.getMonth(),
      calendarDate.getDate(),
      hours,
      minutes,
      seconds,
      options?.endOfSecond ? 999 : 0,
    ),
  );
}

/** Short label for filter chips / triggers: `MMM d HH:mm:ss` in UTC wall clock. */
export function formatUtcFilterInstant(date: Date): string {
  const day = format(utcInstantToLocalCalendarDate(date), "MMM d");
  return `${day} ${formatUtcTimeOfDay(date)}`;
}

/** Expand calendar day picks to inclusive UTC start/end of day. */
export function normalizeCalendarDateRange(
  from: Date | undefined,
  to: Date | undefined,
): { from: Date | undefined; to: Date | undefined } {
  if (!from && !to) return { from: undefined, to: undefined };
  const start = from ?? to!;
  const end = to ?? from!;
  return { from: startOfUtcCalendarDay(start), to: endOfUtcCalendarDay(end) };
}

/**
 * Lists every UTC `YYYY-MM-DD` bucket touched by `[from, to]` (inclusive).
 * Returns [] when the span exceeds `maxDays`.
 */
export function enumerateUtcEventDates(
  from: Date,
  to: Date,
  maxDays: number = MAX_DATE_INDEX_DAYS,
): string[] {
  const startMs = Math.min(from.getTime(), to.getTime());
  const endMs = Math.max(from.getTime(), to.getTime());
  const startDay = toEventDate(new Date(startMs));
  const endDay = toEventDate(new Date(endMs));
  if (!startDay || !endDay) return [];

  const dates: string[] = [];
  let cursor = new Date(`${startDay}T12:00:00.000Z`);
  const endCursor = new Date(`${endDay}T12:00:00.000Z`);

  while (cursor.getTime() <= endCursor.getTime()) {
    if (dates.length >= maxDays) return [];
    dates.push(cursor.toISOString().slice(0, 10));
    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
  }

  return dates;
}

/**
 * Inclusive createdAt window helper (not applied automatically — callers
 * must set dates explicitly when they want a bounded range).
 */
export function getDefaultCreatedRange(
  now: Date = new Date(),
  lookbackDays: number = DEFAULT_LOOKBACK_DAYS,
): { createdFrom: Date; createdTo: Date } {
  const createdTo = new Date(now.getTime());
  const createdFrom = new Date(now.getTime() - lookbackDays * 24 * 60 * 60 * 1000);
  return { createdFrom, createdTo };
}

/** Dashboard analytics / Showing-count default window (last N days). */
export function getDefaultAnalyticsRange(
  now: Date = new Date(),
  lookbackDays: number = ANALYTICS_DEFAULT_LOOKBACK_DAYS,
): { createdFrom: Date; createdTo: Date } {
  return getDefaultCreatedRange(now, lookbackDays);
}

/**
 * Optionally fills in a createdAt lookback. Unused by the dashboard now —
 * kept for scripts/tests that want an explicit default window.
 */
export function applyDefaultCreatedRange<T extends { createdFrom?: Date; createdTo?: Date }>(
  filters: T,
  options?: { lookbackAll?: boolean; now?: Date; lookbackDays?: number },
): T {
  if (options?.lookbackAll) return filters;
  if (filters.createdFrom || filters.createdTo) return filters;
  const { createdFrom, createdTo } = getDefaultCreatedRange(options?.now, options?.lookbackDays);
  return { ...filters, createdFrom, createdTo };
}
